import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface UploadState {
  isUploading: boolean;
  progress: number;
  message: string;
  startTime: number | null;
  error: string | null;
  success: boolean;
}

interface UploadContextValue {
  uploadState: UploadState;
  startUpload: (message?: string) => void;
  updateProgress: (progress: number) => void;
  setError: (error: string) => void;
  setSuccess: (success: boolean) => void;
  completeUpload: () => void;
  cancelUpload: () => void;
}

const UploadContext = createContext<UploadContextValue | undefined>(undefined);

const initialState: UploadState = {
  isUploading: false,
  progress: 0,
  message: 'در حال آپلود پست...',
  startTime: null,
  error: null,
  success: false,
};

export const UploadProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [uploadState, setUploadState] = useState<UploadState>(initialState);

  const startUpload = useCallback((message: string = 'در حال آپلود پست...') => {
    setUploadState({
      isUploading: true,
      progress: 0,
      message,
      startTime: Date.now(),
      error: null,
      success: false,
    });
  }, []);

  const updateProgress = useCallback((progress: number) => {
    setUploadState((prev) => ({
      ...prev,
      progress: Math.min(100, Math.max(0, progress)),
    }));
  }, []);

  const setError = useCallback((error: string) => {
    setUploadState((prev) => ({
      ...prev,
      error,
      isUploading: false,
    }));
  }, []);

  const setSuccess = useCallback((success: boolean) => {
    setUploadState((prev) => ({
      ...prev,
      success,
    }));
  }, []);

  const completeUpload = useCallback(() => {
    setUploadState((prev) => ({
      ...prev,
      isUploading: false,
      progress: 100,
      success: true,
    }));
    
    // Auto-clear after 3 seconds
    setTimeout(() => {
      setUploadState(initialState);
    }, 3000);
  }, []);

  const cancelUpload = useCallback(() => {
    setUploadState(initialState);
  }, []);

  return (
    <UploadContext.Provider
      value={{
        uploadState,
        startUpload,
        updateProgress,
        setError,
        setSuccess,
        completeUpload,
        cancelUpload,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
};

export const useUpload = (): UploadContextValue => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUpload must be used within an UploadProvider');
  }
  return context;
};
